import { Button, Stack, Text } from '@components/ui';
import { formatMoney } from '@features/buyer/hire/formatMoney';
import { HireStepActions } from '@features/buyer/hire/hire-r8tr/hire-step-actions';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import { CardFields } from '@features/payments/card-fields';
import { PaymentCardSkeleton } from '@features/payments/payment-card-skeleton';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { alpha, type Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { useTranslation } from 'react-i18next';

import type { PaymentCard, PaymentJob } from './paymentStepViewTypes';

interface PaymentStepViewProps extends HireStepNavigation {
  createJob: PaymentJob;
  displayCost: number | string;
  discountData: number | string | null;
  discountCriteria: string | null;
  cardsLoading: boolean;
  savedCards: PaymentCard[];
  showPaymentForm: boolean;
  selectedCardToken: string | null;
  setSelectedCardToken: (token: string) => void;
  handleDeleteCard: (token: string) => void;
  setShowPaymentForm: (show: boolean) => void;
  fetchCards: () => void;
  handlePaymentSubmit: () => void;
  loading: boolean;
  isReady: boolean;
}

const badgeSx: SystemStyleObject<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '10px',
  border: 1,
  borderColor: 'primary.main',
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
  px: '7px',
  py: 0.25,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'primary.main',
};

export function PaymentStepView({
  createJob,
  displayCost,
  discountData,
  discountCriteria,
  cardsLoading,
  savedCards,
  showPaymentForm,
  selectedCardToken,
  setSelectedCardToken,
  handleDeleteCard,
  setShowPaymentForm,
  fetchCards,
  handlePaymentSubmit,
  loading,
  isReady,
  onBack,
  onCancel,
}: PaymentStepViewProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Stack gap="md" sx={{ flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          px: 2.5,
          py: 2,
        }}
      >
        <Box>
          <Text as="p" variant="label" sx={{ fontWeight: 700 }}>
            {t('hire.totalDueToday')}
          </Text>
          {discountCriteria ? (
            <Box component="span" sx={[badgeSx, { mt: 0.75 }]}>
              {discountCriteria}
            </Box>
          ) : null}
        </Box>
        <Stack align="end" gap="xs">
          {discountData ? (
            <Text
              variant="caption"
              tone="muted"
              sx={{ textDecoration: 'line-through' }}
            >
              {formatMoney(createJob?.buyerCost)}
            </Text>
          ) : null}
          <Text
            as="p"
            variant="title"
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatMoney(displayCost)}
          </Text>
        </Stack>
      </Box>

      {cardsLoading ? (
        <Stack gap="sm" sx={{ mb: 1 }} aria-busy aria-label={t('hire.loadingCards')}>
          <PaymentCardSkeleton />
        </Stack>
      ) : savedCards.length > 0 && !showPaymentForm ? (
        <>
          <Text variant="label" tone="muted">
            {t('hire.selectPaymentMethod')}
          </Text>
          <Stack gap="sm" sx={{ mb: 1 }}>
            {savedCards.map((card) => {
              const selected = selectedCardToken === card.token;
              return (
                <Box
                  key={card.token}
                  onClick={() => setSelectedCardToken(card.token)}
                  sx={{
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: 1.5,
                    borderRadius: 2,
                    border: 1,
                    borderColor: selected ? 'text.primary' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                    transition:
                      'border-color 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                      borderColor: selected ? 'text.primary' : 'text.secondary',
                    },
                    px: 2,
                    py: 1.75,
                  }}
                >
                  {card.imageUrl ? (
                    <Box
                      component="img"
                      src={card.imageUrl}
                      alt={card.cardType}
                      sx={{
                        height: 24,
                        width: 'auto',
                        flexShrink: 0,
                        objectFit: 'contain',
                        opacity: 0.85,
                      }}
                    />
                  ) : null}
                  <Stack gap="xs" sx={{ flex: 1, gap: '3px' }}>
                    <Text
                      variant="label"
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.75,
                        fontSize: 13,
                      }}
                    >
                      {card.cardType} •••• {card.last4}
                      {card.default ? (
                        <Box component="span" sx={badgeSx}>
                          {t('common.default')}
                        </Box>
                      ) : null}
                    </Text>
                    <Text variant="caption" tone="muted" sx={{ fontSize: 12 }}>
                      Expires {card.expirationDate}
                    </Text>
                  </Stack>
                  <Stack
                    direction="row"
                    align="center"
                    gap="sm"
                    sx={{ flexShrink: 0 }}
                  >
                    {selected ? (
                      <RadioButtonCheckedIcon
                        sx={{ fontSize: 18, color: 'primary.main' }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ fontSize: 18, color: 'text.secondary' }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteCard(card.token);
                      }}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          color: 'primary.main',
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => setShowPaymentForm(true)}
            startIcon={<AddCardOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ mb: 1 }}
          >
            {t('payments.addAnother')}
          </Button>
        </>
      ) : (
        <>
          <Box
            sx={{
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text variant="label" tone="muted">
              {t('hire.paymentMethod')}
            </Text>
            {savedCards.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowPaymentForm(false);
                  fetchCards();
                }}
              >
                ← Back to saved cards
              </Button>
            ) : null}
          </Box>
          <CardFields />
        </>
      )}

      <HireStepActions
        onBack={onBack}
        onCancel={onCancel}
        onPrimary={handlePaymentSubmit}
        primaryLabel={`Pay ${formatMoney(displayCost)}`}
        loading={loading}
        primaryDisabled={
          (showPaymentForm && !isReady) ||
          (!showPaymentForm && !selectedCardToken)
        }
      />

      <Backdrop
        sx={{
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Stack>
  );
}
